import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Container, Paper, Typography, Collapse, Link } from '@mui/material';
import { ErrorOutline, Refresh, Home } from '@mui/icons-material';
import { registrarLogError } from '../../services/api';

// Red de seguridad para toda la app: si una pantalla tira un error al
// renderizar, React desmonta TODO el árbol y queda la página en blanco sin
// ninguna pista. Acá se muestra un cartel "Algo salió mal" y el error se
// guarda en el log de errores (tabla `errores`, pantalla "Registro de
// errores"), así se puede ver qué pasó sin pedirle al usuario que abra F12.
//
// Solo atrapa errores de render/ciclo de vida. Los errores de pedidos a la API
// y de handlers de eventos ya los avisa/loguea el interceptor de services/api.js.

const recortar = (texto, lineas) => String(texto || '').split('\n').slice(0, lineas).join('\n').trim();

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, verDetalle: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Registrar el error nunca puede generar otro error: si falla, queda en la consola.
    try {
      const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
      const usuarioid = userData?.usuarioid || 'unlogin';
      const detalle = JSON.stringify({
        ruta: window.location.href,
        mensaje: String(error?.message || error),
        stack: recortar(error?.stack, 6),
        componentes: recortar(info?.componentStack, 8),
        navegador: navigator.userAgent,
      }, null, 2);
      registrarLogError(usuarioid, `Pantalla:${window.location.pathname}`.slice(0, 100), detalle.slice(0, 3000));
    } catch (e) {
      console.error('No se pudo registrar el error de pantalla:', e);
    }
    console.error('Error en la pantalla:', error, info?.componentStack);
  }

  render() {
    const { error, verDetalle } = this.state;
    if (!error) return this.props.children;

    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center', borderRadius: 3 }}>
          <ErrorOutline color="error" sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Algo salió mal</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Esta pantalla tuvo un error y no se pudo mostrar. El problema ya quedó registrado para que lo revisemos.
            Probá recargar; si sigue pasando, volvé al inicio y avisanos.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Button variant="contained" startIcon={<Refresh />} onClick={() => window.location.reload()}>
              Recargar la página
            </Button>
            <Button variant="outlined" startIcon={<Home />} onClick={() => window.location.assign('/dashboard')}>
              Ir al inicio
            </Button>
          </Box>
          <Link component="button" type="button" variant="caption" color="text.secondary"
            onClick={() => this.setState({ verDetalle: !verDetalle })}>
            {verDetalle ? 'Ocultar detalle técnico' : 'Ver detalle técnico'}
          </Link>
          <Collapse in={verDetalle}>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', mt: 1, textAlign: 'left', wordBreak: 'break-word', fontFamily: 'monospace' }}>
              {window.location.pathname}: {String(error?.message || error)}
            </Typography>
          </Collapse>
        </Paper>
      </Container>
    );
  }
}

// Se reinicia solo al navegar a otra ruta: si una pantalla falló, ir a otra
// (por ejemplo desde un enlace) no deja el cartel pegado.
export function ErrorBoundaryPorRuta({ children }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

export default ErrorBoundary;
